export const RenderReadLengthPlot = (combinedCsv, runid, camera, readLengthMedian) => {
    try {
        if (!combinedCsv) return undefined;

        // Calculate the grand total sum of 'All reads' across both combined cameras
        const combinedGrandTotalAllReads = combinedCsv['All reads'] 
            ? combinedCsv['All reads'].reduce((partialSum, a) => partialSum + a, 0) 
            : 1;

        // Filter out 'Length' scale to cleanly isolate target metric series keys
        function filterIndexes(csv) {
            const filteredCsv = {};
            for (const key in csv) {
                if (key !== 'Length') {
                    filteredCsv[key] = csv[key];
                }
            }
            return filteredCsv;
        }
        let updateCsv = filterIndexes(combinedCsv);

        // Extract the pre-sorted combined horizontal base-pair scale
        const lengths = combinedCsv.Length;
        
        // Define dynamic view limits based on whether readLengthMedian is provided
        const horizontalCutoff = readLengthMedian ? readLengthMedian : Math.max(...lengths);

        // Color Palette Specifications
        const GetLineColor = name => {
            switch (name.trim()) {
                case 'Trimmed by adapter':
                    return 'red';
                case 'Trimmed by quality':
                    return '#8ae1e1';
                case 'Trimmed by Adapter or Quality':
                    return '#1f77b4';
                case 'All reads':
                    return 'black';
                default:
                    return '#1f77b4';
            }
        };

        // Generate Traces using unified percentages relative to the combined grand total
        function GenerateDataByFlow(obj) {
            let data = [];
            let keys = Object.keys(obj);

            keys.forEach((key) => {
                // Find how many array elements fit within our horizontal range view window
                const elementsInWindowCount = lengths.filter(len => len <= horizontalCutoff).length;

                const rawYValues = obj[key].slice(0, elementsInWindowCount);
                const xCoordinates = lengths.slice(0, elementsInWindowCount);

                // Divide by the grand total sum of reads from both combined cameras
                const yPercentages = rawYValues.map(currElement => {
                    return parseFloat(((currElement / combinedGrandTotalAllReads) * 100).toFixed(2));
                });

                data.push({
                    type: "scatter",
                    mode: "lines",
                    connectgaps: false,
                    name: `${key.trim()}`,
                    y: yPercentages, 
                    x: xCoordinates,
                    line: { 
                        color: GetLineColor(key),
                        //width: 2,
                        //shape: "hv" // High-fidelity vertical step-histogram matching
                    }
                });
            });
            return data;
        }

        const data = GenerateDataByFlow(updateCsv);

        const layout = {
            bargap: 0.3,
            margin: { t: 25, b: 35, l: 55, r: 20 },
            titlefont: { "size": 14 },
            height: 500,
            title: `Read-Length Distribution (Bases) - ${runid !== undefined && runid !== null ? runid.split('_')[0] : runid} ${camera !== undefined ? camera : ''}`,
            xaxis: { 
                title: "Read length (bases)",
                range: [0, horizontalCutoff], 
                zeroline: false,
                gridcolor: "rgba(0,0,0,0.1)"
            },
            yaxis: {
                title: "% of reads", 
                zeroline: false,
                gridcolor: "rgba(0,0,0,0.1)",
                titlefont: { "size": 12 }
            },
            hovermode: "closest",
            plot_bgcolor: "#ffffff"
        };

        const config = {
            modeBarButtonsToRemove: ['sendDataToCloud', 'displaylogo', 'pan2d', 'pan3d', 'autoScale2d', 'resetScale2d'],
            displaylogo: false,
            displayModeBar: true,
            responsive: true
        };        

        return { data, layout, config };
    }
    catch (e) {
        return undefined;
    }
};

export const RenderHomoIndelErrorPlot = (csv, runid, camera) => {
    try {
        const makeflowbyIndex = (rows, idx) => {
            return rows.map((currElement, index) => {
                return index + idx;
            })
        }

        const unpackByCaption = (rows, caption) => {
            let data = [];
            rows.filter(function (row, index) {
                if (caption === 'T' && index % 4 === 0) {
                    data.push(row);
                    data.push(row);
                    data.push(row);
                    data.push(row);
                }
                else if (caption === 'G' && index % 4 === 1) {
                    data.push(row);
                    data.push(row);
                    data.push(row);
                    data.push(row);
                }
                else if (caption === 'C' && index % 4 === 2) {
                    data.push(row);
                    data.push(row);
                    data.push(row);
                    data.push(row);
                }
                else if (caption === 'A' && index % 4 === 3) {
                    data.push(row);
                    data.push(row);
                    data.push(row);
                    data.push(row);
                }
            });

            return data;
        }

        const GetLineColor = name => {
            switch (name) {
                case 'T':
                    return 'red';
                case 'A':
                    return 'green';
                case 'C':
                    return '#1f77b4';
                case 'G':
                    return 'black';
                default:
                    return '#1f77b4';
            }
        }

        const GenerateDataByFlow = (obj) => {
            let data = [];
            let keys = Object.keys(obj);
            let values = Object.values(obj);

            keys.forEach((key, i) => {
                let formattedValues = values[i].map(value => parseFloat(value).toFixed(2)); 
                let temp = {
                    type: "scatter",
                    mode: "lines",
                    connectgaps: false,
                    name: `${key}`,
                    x: makeflowbyIndex(values[i], keys.length + 1),
                    y: formattedValues,
                    line: { color: GetLineColor(key) },
                }
                data.push(temp);
            });
            return data;
        }

        let T = unpackByCaption(csv, 'T');
        let G = unpackByCaption(csv, 'G');
        let C = unpackByCaption(csv, 'C');
        let A = unpackByCaption(csv, 'A');

        const data = GenerateDataByFlow({T, G,C,A });

        const layout = {
            bargap: 0.3,
            margin: {
                t: 25,
                b: 35,
                l: 55
            },
            titlefont: {
                "size": 14
            },
            height: 500,
            title: `HP Indel Error per flow - ${runid !== undefined && runid !== null ? runid.split('_')[0] : runid} ${camera !== undefined ? camera : ''}`,
            xaxis: { title: "Flows" },
            yaxis: {
                title: "BER (%)",
                titlefont: {
                    "size": 12
                },
                //tickformat: '.2s' 
            },
        };

        const config = {
            modeBarButtonsToRemove: ['sendDataToCloud', 'displaylogo', 'pan2d', 'pan3d', 'autoScale2d', 'resetScale2d'],
            displaylogo: false,
            displayModeBar: true
        };

        return { data, layout, config };
    }
    catch (e) {       
        return undefined;
     }
}

export const RenderReadLengthBaseQualityPlot = (csv, runid, camera, barcode, averageReadLength) => {
    try {       
        const xAxisLimit = averageReadLength;

        let data = [{
            type: "scatter",
            mode: "lines",
            connectgaps: false,
            name: `Base Quality`,
            x: csv.map((item, i) => {
                const xValue = i + 1;
                if (xValue <= xAxisLimit) {
                    return xValue;
                } else {
                    return null; // This will create a gap in the line for values beyond the limit
                }
            }),
            y: csv,
        }];

        let layout = {
            bargap: 0.3,
            margin: {
                t: 25,
                b: 35,
                l: 55
            },
            titlefont: {
                "size": 14
            },
            height: 500,
            title: `Base Quality<br> ${runid !== undefined && runid !== null ? runid.split('_')[0] : runid}: ${barcode}`,
            xaxis: { 
                title: "Read Length",
                range: [0, xAxisLimit] // Limit x-axis to averageReadLength * 1.5
            },
            yaxis: {
                title: "Base Quality",
                titlefont: {
                    "size": 12
                }
            },
        };

        let config = {
            modeBarButtonsToRemove: ['sendDataToCloud', 'displaylogo', 'pan2d', 'pan3d', 'autoScale2d', 'resetScale2d'],
            displaylogo: false,
            displayModeBar: true
        };

        return { data, layout, config };
    }
    catch (e) {
        return undefined;
     }
}


export const RenderReadLengthBarcodeDetailsPlot = (csv, runid, barcode, averageReadLength) => {
    try { 
        var data = [{
            type: "scatter",
            mode: "lines",
            connectgaps: false,
            name: `Raw Read Length`,
            x: csv.read_length.map((item, i) => i + 1),
            y: csv.read_length,
        },
        {
            type: "scatter",
            mode: "lines",
            connectgaps: false,
            name: `Aligned Read Length`,
            x: csv.aligned_read_length.map((item, i) => i + 1),
            y: csv.aligned_read_length,
        }];

        var layout = {
            bargap: 0.3,
            margin: {
                t: 25,
                b: 35,
                l: 55
            },
            titlefont: {
                "size": 14
            },
            height: 500,
            title: `Average Read Length<br> ${runid !== undefined && runid !== null ? runid.split('_')[0] : runid}: ${barcode}`,
            xaxis: { 
                title: "Read Length",
                range: [0, averageReadLength]  // Set x-axis range from 0 to averageReadLength
            },
            yaxis: {
                tickformat: '.3s'
            }
        };

        var config = {
            modeBarButtonsToRemove: ['sendDataToCloud', 'displaylogo', 'pan2d', 'pan3d', 'autoScale2d', 'resetScale2d'],
            displaylogo: false,
            displayModeBar: true
        };

        return { data, layout, config };
    }
    catch (e) {       
        return undefined;
     }
}